import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { isEmpty, isEqual, tap } from 'lodash';

import SVGIcon from 'components/svg-icon/SVGIcon';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import { withModal } from 'components/modal/ModalHOC';
import ConsentPlaceholder from 'components/consent-placeholder/ConsentPlaceholder';

import { deletePosts } from 'redux-store/reducers/instagramPostsDeleteReducer';

import { isDefined } from 'utils/helper/common';
import { buildImage } from 'utils/image';
import { __t, applyTranslations } from 'utils/language';
import { WITH_GALLERY_MODAL_CONFIG } from 'utils/gallery';
import { getPostsToDelete } from 'utils/posts';
import { extendWithInstagramThumbnailUrl } from 'utils/content/thumbnails';
import { CHANNEL_TYPE_INSTAGRAM } from 'utils/channels';

import './creator-tile-image-grid.scss';

import type { ImageCandidate, ImageStandard } from 'types';
import type { ConsentReducerConsentsMap, ConsentReducerSupportedChannels } from 'redux-store/types/ConsentReducer.type';

applyTranslations(require('./creator-tile-image-grid.i18n.json'));

const ImageWithModal = withModal(<div />, WITH_GALLERY_MODAL_CONFIG);
const NUM_POSTS_TO_LOAD: number = 15;
const NUM_POSTS_TO_SHOW: number = 9;

type OwnProps = {
  images: Array<ImageCandidate>,
  columns?: number,
  rows?: number,
  count?: number,
  gutter?: number
};

type StateProps = {
  userChannelConsents: ConsentReducerConsentsMap
};

type DispatchProps = {
  dispatchDeleteInstagramPosts: (ids: Array<string>) => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  images: Array<ImageStandard | ImageCandidate>
};

class CreatorTileImageGrid extends React.PureComponent<Props, State> {
  numImagesToShow: number = this.props.count || NUM_POSTS_TO_SHOW;

  constructor(props: Props) {
    super(props);

    const imagesLoading = new Array(this.numImagesToShow).fill({
      type: 'loading',
      assetUrl: '',
      code: '',
      url: ''
    });

    this.state = {
      images: imagesLoading
    };
  }

  componentDidMount() {
    const { images, userChannelConsents } = this.props;

    if (userChannelConsents[CHANNEL_TYPE_INSTAGRAM]) {
      this.fetchImages();
    } else {
      this.setState({
        images: this.addPlaceholderImages(images).slice(0, this.numImagesToShow)
      });
    }
  }

  componentDidUpdate(prevProps) {
    const { images } = this.props;

    if (!isEqual(images, prevProps.images)) {
      this.fetchImages();
    }
  }

  fetchImages = (): void => {
    const { images } = this.props;
    const imageObjects = images.map(image => buildImage(image));
    const preloadedImages = this.preloadImages(imageObjects);

    preloadedImages.then(preloads => {
      this.setState({
        images: this.addPlaceholderImages(preloads).slice(0, this.numImagesToShow)
      });
    });
  };

  addPlaceholderImages = (images: Array<ImageStandard | ImageCandidate>): Array<ImageStandard | ImageCandidate> => {
    const updatedImages = [...images];
    const placeholderCount = this.numImagesToShow - updatedImages.length;

    for (let i = 0; i < placeholderCount; i++) {
      updatedImages.push({
        code: `placeholder-image-${i}`,
        type: 'placeholder',
        assetUrl: '',
        url: ''
      });
    }

    return updatedImages;
  };

  buildImageArray(posts: Array<ImageStandard>): Array<ImageStandard> {
    return posts.filter(post => post.assetUrl);
  }

  preloadImages = (images: Array<ImageStandard>): Promise<Array<ImageStandard>> => {
    const promises: Array<Promise<ImageStandard>> = [];

    for (let i = 0; i < NUM_POSTS_TO_LOAD; i++) {
      if (images[i]) {
        promises.push(extendWithInstagramThumbnailUrl(images[i]));
      }
    }

    return Promise.all(promises)
      .then(results => tap(results, this.deleteUnavailablePosts))
      .then(results => this.buildImageArray(results));
  };

  deleteUnavailablePosts = (posts: Array<ImageStandard>): void => {
    const { dispatchDeleteInstagramPosts } = this.props;

    const postsWithCodes = posts.map(post => ({
      code: post.code,
      assetUrl: post.assetUrl
    }));

    const postsToDelete = getPostsToDelete(postsWithCodes);

    if (!isEmpty(postsToDelete)) {
      dispatchDeleteInstagramPosts(postsToDelete);
    }
  };

  renderImagePlaceholder(): React.ReactNode {
    return (
      <div className="creator-tile-image-grid__item-placeholder">
        <div className="creator-tile-image-grid__icon">
          <SVGDynamicIcon
            name="puzzle-piece-custom-fg"
            width={22}
            height={22}
            customColor="grey-light"
          />
        </div>
        <span>
          {__t('creator_tile_image_grid.image_placeholder_label')}
        </span>
      </div>
    );
  }

  renderImageTile(image: ImageStandard | ImageCandidate, index: number): React.ReactNode {
    const { userChannelConsents } = this.props;
    const { images } = this.state;

    if (image.type && !userChannelConsents[image.type]) {
      return (
        <ConsentPlaceholder
          withLearnMore
          link={image.url}
          type={image.type as ConsentReducerSupportedChannels}
        />
      );
    }

    return (
      <ImageWithModal
        className="creator-tile-image-grid__item"
        data-qa={`creator-tile-image-grid__item-${index}`}
        modalContentProps={{
          items: images ? images.filter(i => i.type !== 'placeholder') : [],
          currentPosition: index
        }}
        style={{ backgroundImage: `url(${image.assetUrl})` }}
      >
        {
          image.type === 'youtube' && (
            <div
              className="creator-tile-image-grid__overlay"
              data-qa={`creator-tile-image-grid__overlay-${index}`}
            >
              <SVGIcon
                name="play-button"
                width={40}
                height={40}
              />
            </div>
          )
        }
      </ImageWithModal>
    );
  }

  render(): React.ReactNode {
    const { images } = this.state;
    const { columns, rows, gutter } = this.props;
    const showPrevNextArrows = images && images.length > this.numImagesToShow;

    const gridItemsClasses = classnames({
      'creator-tile-image-grid__items': true,
      'creator-tile-image-grid__items--2-columns': columns === 2,
      'creator-tile-image-grid__items--2-rows': rows === 2
    });

    return (
      <div
        className="creator-tile-image-grid"
        data-qa="creator-tile-image-grid"
      >
        <div
          className="creator-tile-image-grid--prev"
          data-qa="creator-tile-image-grid--prev"
        >
          {
            showPrevNextArrows && (
              <SVGDynamicIcon
                name="chevron-left-custom-fg"
                width={10}
                height={20}
                customColor="grey-light"
              />
            )
          }
        </div>
        <div
          className={gridItemsClasses}
          data-qa="creator-tile-image-grid__items"
          style={{ gridGap: gutter }}
        >
          {
            (images || []).filter(isDefined).map((image, index) => (
              <div
                key={`${image.code}-${index}`}
                className="creator-tile-image-grid__loading"
              >
                {
                  image.type === 'placeholder' ?
                    this.renderImagePlaceholder() :
                    this.renderImageTile(image, index)
                }
              </div>
            ))
          }
        </div>
        <div
          className="creator-tile-image-grid--next"
          data-qa="creator-tile-image-grid--next"
        >
          {
            showPrevNextArrows && (
              <SVGDynamicIcon
                name="chevron-right-custom-fg"
                width={10}
                height={20}
                customColor="grey-light"
              />
            )
          }
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ consentReducer }): StateProps => ({
  userChannelConsents: consentReducer.consents
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchDeleteInstagramPosts: (posts: Array<string>) => (
    dispatch(deletePosts(posts, 'instagram'))
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(CreatorTileImageGrid);
